import { useCallback, useEffect, useState } from "react";
import type { DocumentDto } from "@gatiq/shared";
import { FileText, Image as ImageIcon, Link2 } from "lucide-react";
import { Alert, Card, EmptyState, Skeleton } from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { KarigarShell } from "@/components/KarigarShell";
import { useLanguage } from "@/i18n/language";

/**
 * The published documents, read on the phone (D-23).
 *
 * Each item opens through the authenticated route, including external links.
 */
export function KarigarDocumentsPage() {
  const { t } = useLanguage("hi");
  const [docs, setDocs] = useState<DocumentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ items: DocumentDto[] }>("/org/me/documents");
      setDocs(res.items);
      setFailed(false);
    } catch (err) {
      setFailed(true);
      if (err instanceof ApiClientError) setDocs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <KarigarShell active="home" title={t("दस्तावेज़", "Documents")}>
      {failed ? (
        <Alert kind="danger">{t("इंटरनेट देखें और दोबारा कोशिश करें।", "Check your internet and try again.")}</Alert>
      ) : loading ? (
        <Card>
          <div className="flex flex-col gap-3 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </Card>
      ) : docs.length === 0 ? (
        <EmptyState
          title={t("अभी कोई दस्तावेज़ नहीं", "No documents yet")}
          message={t(
            "जब आपकी कंपनी कोई PDF, तस्वीर या लिंक साझा करेगी, यहाँ दिखेगा।",
            "When your company publishes a PDF, image or link, it shows up here.",
          )}
        />
      ) : (
        <Card>
          <ul className="divide-y divide-rule/70">
            {docs.map((doc) => (
              <li key={doc.id}>
                <a
                  className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-panel/60"
                  href={`/api/org/me/documents/${doc.id}/file`}
                  rel="noreferrer"
                  target="_blank"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand">
                    {doc.kind === "link" ? <Link2 className="size-[1.125rem]" aria-hidden="true" /> : doc.kind === "image" ? <ImageIcon className="size-[1.125rem]" aria-hidden="true" /> : <FileText className="size-[1.125rem]" aria-hidden="true" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-title text-[15px] font-semibold text-ink">
                      {doc.title}
                    </span>
                    {doc.description ? (
                      <span className="mt-0.5 block truncate text-xs text-muted">{doc.description}</span>
                    ) : null}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </KarigarShell>
  );
}
